import React from "react";
import {
  X,
  Calendar,
  Building2,
  FileText,
  ChevronRight,
  BadgeInfo,
} from "lucide-react";
import { ClinicScheduleStatusDto } from "../../web-api-client";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { getClinicDefaultimg } from "../../utils/defaultImage";
import { ClinicImage } from "../ui/ClinicImage";

interface ClinicDetailModalProps {
  clinic?: ClinicScheduleStatusDto | null;
  isOpen: boolean;
  onTrigger: (open: boolean) => void;
  type: string;
  onRegister: (clinic: ClinicScheduleStatusDto) => void;
}

export default function ClinicDetailModal({
  clinic,
  isOpen,
  onTrigger,
  type,
  onRegister,
}: ClinicDetailModalProps) {
  if (!isOpen || !clinic || type !== "detail") return null;

  const description = clinic.description?.trim()
    ? clinic.description
    : "Phòng khám tiếp nhận thăm khám, tư vấn và hỗ trợ người bệnh theo chuyên khoa.";

  return (
    <Dialog open={isOpen} onOpenChange={onTrigger}>
      <DialogContent className="w-[94vw] max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-white p-0 shadow-2xl">
        <DialogHeader className="sr-only">
          <DialogTitle>{clinic.clinicName ?? "Chi tiết phòng khám"}</DialogTitle>
          <DialogDescription>
            Thông tin chi tiết phòng khám và thao tác đặt khám
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <div className="relative h-52 w-full overflow-hidden md:h-64">
            <ClinicImage
              src={clinic.imageUrl ?? getClinicDefaultimg()}
              alt={clinic.clinicName ?? ""}
              fallbackSrc={getClinicDefaultimg()}
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          </div>

          <DialogClose
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-md transition hover:bg-white"
            aria-label="Đóng"
          >
            <X className="h-5 w-5" />
          </DialogClose>

          <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm ring-1 ring-white/20">
              <Building2 className="h-3.5 w-3.5" />
              Phòng khám chuyên khoa
            </div>

            <DialogTitle className="text-left text-2xl font-bold leading-tight text-white md:text-3xl">
              {clinic.clinicName}
            </DialogTitle>
          </div>
        </div>

        <div className="space-y-5 p-5 md:space-y-6 md:p-6">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-5">
            <div className="mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-green-600" />
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800">
                Chức năng
              </h3>
            </div>

            <p className="min-h-44 max-h-44 h-44 overflow-auto pr-1 text-sm leading-7 text-slate-600 md:text-[15px]">
              {description}
            </p>
          </div>

          {/* <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-emerald-600 shadow-sm">
                  <BadgeInfo className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">
                    Khám theo chuyên khoa
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Xem thông tin và đăng ký khám phù hợp với nhu cầu của bạn.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-700">
                  <Calendar className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Đặt khám nhanh</p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Tiếp tục để chọn lịch khám phù hợp và hoàn tất đăng ký.
                  </p>
                </div>
              </div>
            </div>
          </div> */}

          <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => onTrigger(false)}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Đóng
            </button>

            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 hover:shadow-md active:scale-[0.99]"
              onClick={() => onRegister(clinic)}
            >
              <Calendar className="h-4 w-4" />
              Đặt khám ngay
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}